// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import translate from "@insite/client-framework/Translate";
import { AddressFieldDisplayCollectionModel } from "@insite/client-framework/Types/ApiModels";
import Button from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomCustomerAddressForm from "@steiner/App/Components/CustomCreateAccount/CustomCustomerAddressForm";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { initialAddress } from "@steiner/App/Widgets/CreateAccount/CreateCustomerNewAccount";
import { USA_ABBR } from "@steiner/XDL/constants";
import { XAddressModel } from "@steiner/XDL/types";
import { xValidateOnSubmit } from "@steiner/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    return {
        countries: getCurrentCountries(state),
        billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
    };
};

interface OwnProps {
    onSubmit: (values: XAddressModel) => void;
    onBackClick: () => void;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface BillingInfoFormStyles {
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    fullGridItem?: GridItemProps;
    buttonsWrapper?: InjectableCss;
}

export const billingInfoFormStyles: BillingInfoFormStyles = {};

const styles = billingInfoFormStyles;

const billingAddressFormId = "newAccountBillingInfo";

const BillingInfoForm = ({ onSubmit, onBackClick, countries, billToAddressFields }: Props) => {
    const [billingAddress, setBillingAddress] = useState(initialAddress);
    useEffect(() => {
        if (countries) {
            const USA = countries.find(i => i.abbreviation === USA_ABBR) || initialAddress.country;
            setBillingAddress({ ...billingAddress, country: USA });
        }
    }, []);

    if (!countries || !billToAddressFields) {
        return null;
    }

    const handleSubmit = () => {
        const billingAddressErrors = xValidateOnSubmit(billingAddressFormId);
        if (billingAddressErrors.length > 0) {
            return;
        }
        onSubmit(billingAddress);
    };

    const xBillToAddressFields: AddressFieldDisplayCollectionModel = {
        ...billToAddressFields,
        firstName: { ...billToAddressFields.firstName, isVisible: false } as any,
        lastName: { ...billToAddressFields.lastName, isVisible: false } as any,
        companyName: { ...billToAddressFields.companyName, isVisible: false } as any,
        email: { ...billToAddressFields.email, isVisible: false } as any,
        phone: { ...billToAddressFields.phone, isVisible: false } as any,
        country: { ...billToAddressFields.country, isVisible: false } as any,
    };

    return (
        <>
            <GridContainer {...styles.container}>
                <GridItem {...styles.fullGridItem}>
                    <CustomCustomerAddressForm
                        formId={billingAddressFormId}
                        countries={countries}
                        fieldDisplay={xBillToAddressFields}
                        address={billingAddress}
                        onAddressChange={address => setBillingAddress(address)}
                        onSubmit={handleSubmit}
                        onCancel={onBackClick}
                    />
                </GridItem>
            </GridContainer>
        </>
    );
};

export default connect(mapStateToProps)(BillingInfoForm);
